import 'inter-ui/inter.css';
import { createTheme, darkTheme, genPageTheme, shapes } from '@backstage/theme';
import { BackstageOverrides } from '@backstage/core-components';
import './style.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './ag-grid-theme-deepc.css';
import './ag-grid-gateconfig.css';

// Semantic colours, shared by `palette.error/warning/success` and `palette.status`
// so the two cannot drift apart. `palette.error.main` in particular feeds every
// MUI error state — label, helper text and outline — so a background colour here
// renders invalid fields as if they were disabled.
const STATUS_OK = '#31c48d';
const STATUS_WARNING = '#E3A008';
const STATUS_ERROR = '#F05252';

const deepcTheme = createTheme({
  fontFamily: 'Inter',
  palette: {
    ...darkTheme.palette,
    type: 'dark',
    common: {
      black: '#212132',
      white: '#A6A6AD',
    },
    primary: {
      light: '#A6A6AD',
      dark: '#A6A6AD',
      main: '#9894f9',
      contrastText: '#fff',
    },
    secondary: {
      light: '#A6A6AD',
      dark: '#A6A6AD',
      main: '#A6A6AD',
      contrastText: '#fff',
    },
    error: {
      main: STATUS_ERROR,
    },
    warning: {
      main: STATUS_WARNING,
    },
    info: {
      light: '#fff',
      dark: '#fff000',
      main: '#fff',
      contrastText: '#fff',
    },
    success: {
      main: STATUS_OK,
    },
    background: {
      default: '#181829',
      paper: '#181829',
    },
    errorBackground: '#212132',
    warningBackground: '#8f5e15',
    infoBackground: '#343b58',
    navigation: {
      background: '#212132',
      indicator: '#9894f9',
      color: '#b7b7bd',
      selectedColor: '#fff',
      navItem: {
        hoverBackground: '#373745',
      },
    },
    status: {
      ok: STATUS_OK,
      warning: STATUS_WARNING,
      error: STATUS_ERROR,
      pending: '#08b0e3',
      running: '#08b0e3',
      aborted: STATUS_ERROR,
    },
  },
  defaultPageTheme: 'tool',
  pageTheme: {
    home: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    documentation: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.round,
      options: { fontColor: '#fff' },
    }),
    service: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    website: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    library: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    other: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    app: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
    apis: genPageTheme({
      colors: ['#212132', '#343b58'],
      shape: shapes.wave,
      options: { fontColor: '#fff' },
    }),
  },
});

export const createCustomThemeOverrides = (): BackstageOverrides => {
  return {
    BackstageHeader: {
      header: {
        backgroundImage: `url('/backoffice_header.png')`,
      },
    },
    BackstageSidebar: {
      drawer: {
        boxShadow: '0 0 10px #00000080',
        background: '#212132;',
      },
    },
    MuiPaper: {
      root: {
        boxShadow: 'none !important',
      },
    },
    BackstageTabbedCard: {
      root: {
        background: '#212132',
      },
      indicator: {
        height: '4px',
        backgroundColor: '#9894f9',
      },
    },
    MuiTabs: {
      flexContainer: {
        minHeight: '64px !important',
        marginRight: '42px',
        color: '#b7b7bd !important',
      },
    },
    MuiTab: {
      selected: {
        color: '#fff !important',
      },
    },
    BackstageContent: {
      root: {
        backgroundColor: '#181829',
      },
    },
    BackstageTable: {
      root: {
        width: '100%',
      },
    },
    MuiTableCell: {
      head: {
        background: '#373745 !important',
        border: 'none !important',
      },
      body: {
        padding: '12px 16px',
      },
      root: {
        wordBreak: 'break-word',
      },
    },
    MuiAccordionSummary: {
      content: {
        gap: '32px',
        alignItems: 'center',
      },
    },
    MuiTableRow: {
      root: {
        backgroundColor: '#212132',
      },
      hover: {
        '&:hover': {
          backgroundColor: '#373745 !important',
        },
      },
    },
  };
};

deepcTheme.overrides = {
  ...createCustomThemeOverrides(),
};

export { deepcTheme };